import { ref } from 'vue';
import { useQuasar } from 'quasar';
import { authenticatedFetch } from '@/utils/apiHelper';

/**
 * Composable for managing equipment-to-controller assignments
 * Handles validation, assignment, unassignment, and I/O capacity tracking
 */
export function useEquipmentAssignment() {
  const $q = useQuasar();
  const loading = ref(false);
  const validating = ref(false);

  /**
   * Assign controller to equipment
   */
  async function assignController(equipmentId, controllerTemplateId, ioMapping = null) {
    loading.value = true;
    try {
      const response = await authenticatedFetch(`/api/v2/site-equipment/${equipmentId}/assign-controller`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          controller_template_id: controllerTemplateId,
          io_mapping: ioMapping,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to assign controller');
      }

      const data = await response.json();

      $q.notify({
        type: 'positive',
        message: 'Controller assigned successfully',
        timeout: 2000,
      });

      return data.data;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: error.message || 'Failed to assign controller',
        timeout: 3000,
      });
      throw error;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Unassign controller from equipment
   */
  async function unassignController(equipmentId, deleteController = false) {
    loading.value = true;
    try {
      const response = await authenticatedFetch(`/api/v2/site-equipment/${equipmentId}/unassign-controller?delete_controller=${deleteController}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to unassign controller');
      }

      const data = await response.json();

      $q.notify({
        type: 'positive',
        message: 'Controller unassigned successfully',
        timeout: 2000,
      });

      return data.data;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: error.message || 'Failed to unassign controller',
        timeout: 3000,
      });
      throw error;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Reassign controller to different equipment
   */
  async function reassignController(controllerId, newEquipmentId) {
    loading.value = true;
    try {
      const response = await authenticatedFetch(`/api/v2/site-equipment/${controllerId}/reassign-controller`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          new_equipment_id: newEquipmentId,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to reassign controller');
      }

      const data = await response.json();

      $q.notify({
        type: 'positive',
        message: 'Controller reassigned successfully',
        timeout: 2000,
      });

      return data.data;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: error.message || 'Failed to reassign controller',
        timeout: 3000,
      });
      throw error;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Validate controller assignment (check I/O capacity)
   */
  async function validateAssignment(equipmentId, controllerTemplateId) {
    validating.value = true;
    try {
      const response = await authenticatedFetch(`/api/v2/site-equipment/${equipmentId}/validate-controller-assignment?controller_template_id=${controllerTemplateId}`, {
        method: 'GET',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Validation failed');
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: error.message || 'Validation failed',
        timeout: 3000,
      });
      throw error;
    } finally {
      validating.value = false;
    }
  }

  /**
   * Get I/O requirements for equipment
   */
  async function getIoRequirements(equipmentId) {
    try {
      const response = await authenticatedFetch(`/api/v2/site-equipment/${equipmentId}/io-requirements`, {
        method: 'GET',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to get I/O requirements');
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: error.message || 'Failed to get I/O requirements',
        timeout: 3000,
      });
      throw error;
    }
  }

  /**
   * Get color for I/O type badge
   */
  function getIoTypeColor(ioType) {
    const colors = {
      AI: 'blue',
      AO: 'green',
      DI: 'orange',
      DO: 'red',
    };
    return colors[ioType] || 'grey';
  }

  /**
   * Format I/O capacity for display
   */
  function formatIoCapacity(capacity) {
    if (!capacity) {return 'N/A';}
    const { AI = 0, AO = 0, DI = 0, DO = 0 } = capacity;
    return `${AI} AI, ${AO} AO, ${DI} DI, ${DO} DO`;
  }

  /**
   * Calculate total I/O points
   */
  function getTotalIoPoints(capacity) {
    if (!capacity) {return 0;}
    const { AI = 0, AO = 0, DI = 0, DO = 0 } = capacity;
    return AI + AO + DI + DO;
  }

  return {
    loading,
    validating,
    assignController,
    unassignController,
    reassignController,
    validateAssignment,
    getIoRequirements,
    getIoTypeColor,
    formatIoCapacity,
    getTotalIoPoints,
  };
}
